import React, { useContext, useMemo } from 'react';
import Routes from 'Routes';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import { APP } from 'appenv';
import { useSelector } from 'react-redux';
import { UserSessionContext } from 'components/UserSession';
import {
  searchResultsSelector,
  selectParticipantsFromSearchResults,
} from 'models/searchResults';
import { useTranslation } from 'react-i18next';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocale } from 'models/localization';
import { selectAllBoothTiers } from 'models/cms/boothTiers/selectors';

import GenericSearchResultItem from 'components/search/GenericSearchResultItem';
import WebinarSearchResultItem from 'components/search/WebinarSearchResultItem';
import ParticipantSearchResultItem from 'components/search/ParticipantSearchResultItem';
import EmptyStatePlaceholder from 'components/search/EmptyStatePlaceholder';
import SpeakerSearchResultItem from 'components/search/SpeakerSearchResultItem';
import { nameByLocale } from 'hooks/useNameByLocale';

const RootContainer = styled.div`
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ListContainer = styled.div`
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
`;

const StyledSubHeader = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 14px;
  color: #999;
  margin: 8px 16px;
`;

const ViewAllResultsItem = withStyles(() => ({
  root: {
    fontFamily: 'Lato',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '14px',
    color: '#5C8DF3',
    textAlign: 'center',
    paddingTop: '13px',
    paddingBottom: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F6F6F6',
    flex: '0 0 auto',
    width: '100%',
  },
}))(MenuItem);

const pickFromExhibitor = (exhibitor, allTiers, locale) => {
  const target = exhibitor.appliedDesignCodename === '002'
    ? 'middle_banner'
    : 'top_banner';

  const logo = (exhibitor.logo?.values?.filter((i) => i.locale === locale)[0]?.value?.originalImageUrl)
    || (exhibitor.designElements || [])
      .find((node) => node.name === target)
      ?.image?.[locale]?.originalImageUrl;

  const tierObj = allTiers.find((tier) => tier?.id === exhibitor?.tier?.id);

  const boothName = exhibitor.name?.values.filter((i) => i.locale === locale)[0].value;
  return {
    name: boothName || null,
    logo,
    description: '',
    tier: (tierObj?.nameVisible ? tierObj?.name?.[locale] : null),
  };
};

const SearchResultList = ({
  navigateToAllResults,
}) => {
  const locale = useSelector(selectLocale);
  const {
    exhibitors,
    webinars,
    products,
    speakers,
  } = useSelector(searchResultsSelector);
  const { canSee, userSession: { role } } = useContext(UserSessionContext);
  const participants = useSelector(selectParticipantsFromSearchResults(role));
  const { pageNames, features } = useSelector((s) => s.eventConfigs);
  const allTiers = useSelector(selectAllBoothTiers);

  const canSeeExhibitors = canSee('hall');
  const haveValidExhibitors = exhibitors.some((e) => !!(e.name?.values.filter((i) => i.locale === locale)[0].value));

  const showExhibitors = exhibitors.length > 0 && haveValidExhibitors && canSeeExhibitors;
  const canSeeWebinars = canSee('webinar') && APP !== 'ms624';
  const showWebinars = webinars.length > 0 && canSeeWebinars;
  const canSeeParticipants = canSee('participant');
  const showParticipants = participants.length > 0 && canSeeParticipants;
  const showProducts = features.includes('productPage') && !!products.length;
  const showSpeakers = canSee('webinar') && speakers.length > 0;
  const showResults = showExhibitors || showWebinars || showParticipants || showProducts || showSpeakers;
  const { t } = useTranslation();
  const exhibitorName = useMemo(() => pageNames.exhibitors?.[locale] || t('page.exhibitors', 'Exhibitors'), [locale, pageNames.exhibitors, t]);
  const webinarName = useMemo(() => pageNames.webinar?.[locale] || t('page.webinar', 'Webinars'), [locale, pageNames.webinar, t]);
  const participantName = useMemo(() => pageNames.participant?.[locale] || t('page.participants', 'Participants'), [locale, pageNames.participant, t]);
  const productsName = useMemo(() => pageNames.products?.[locale] || t('page.products', 'Products'), [locale, pageNames.products, t]);
  const speakersName = useMemo(() => pageNames.speakers?.[locale] || t('page.speakers', 'Speakers'), [locale, pageNames.speakers, t]);

  return (
    <RootContainer>
      {
        showResults ? (
          <>
            <ListContainer>
              {
                showExhibitors && (
                  <>
                    <StyledSubHeader>
                      {exhibitorName}
                    </StyledSubHeader>
                    {
                      exhibitors.map((exhibitor) => {
                        const {
                          name,
                          logo,
                          description,
                          tier,
                        } = pickFromExhibitor(exhibitor, features.includes('boothTiers') ? allTiers : [], locale);

                        return name ? (
                          <GenericSearchResultItem
                            key={exhibitor.id}
                            id={`${exhibitor.id}`}
                            name={name}
                            to={Routes.booth(exhibitor.id)}
                            logo={logo}
                            tier={tier}
                            description={description}
                            variant="compact"
                          />
                        ) : null;
                      })
                    }
                  </>
                )
              }
              {
                showWebinars && (
                  <>
                    <StyledSubHeader>
                      {webinarName}
                    </StyledSubHeader>
                    {
                      webinars.map(({
                        id,
                        kind,
                        startAt,
                        endAt,
                        timezone,
                        translations,
                      }) => {
                        const { title } = translations[locale] || {};
                        return (
                          <WebinarSearchResultItem
                            key={id}
                            type={kind}
                            id={`${id}`}
                            name={title}
                            startAt={startAt}
                            endAt={endAt}
                            timezone={timezone}
                            variant="compact"
                          />
                        );
                      })
                    }
                  </>
                )
              }
              {
                showSpeakers && (
                  <>
                    <StyledSubHeader>
                      {speakersName}
                    </StyledSubHeader>
                    {
                      speakers.map(({
                        id,
                        webinarId,
                        translations,
                        // eslint-disable-next-line camelcase
                        profile_picture_url,
                      }) => {
                        const { name, company, title } = translations[locale] || {};
                        return (
                          <SpeakerSearchResultItem
                            key={id}
                            // eslint-disable-next-line camelcase
                            picture={profile_picture_url}
                            id={`${id}`}
                            webinarId={`${webinarId}`}
                            name={name}
                            company={company}
                            title={title}
                          />
                        );
                      })
                    }
                  </>
                )
              }
              {
                showParticipants && (
                  <>
                    <StyledSubHeader>
                      {participantName}
                    </StyledSubHeader>
                    {
                      participants.map((participant) => {
                        const {
                          firstName,
                          lastName,
                          uuid,
                          customAttendeeFields: {
                            vexpo_chat_tag: fallbackTag,
                            vexpo_chat_user_description: fallbackDescription,
                          },
                          vexpoChatTag,
                          vexpoChatUserDescription,
                        } = participant;
                        return (
                          <ParticipantSearchResultItem
                            key={uuid}
                            uuid={uuid}
                            name={nameByLocale(firstName, lastName, locale)}
                            userTag={vexpoChatTag || fallbackTag}
                            userDescription={vexpoChatUserDescription || fallbackDescription}
                            variant="compact"
                          />
                        );
                      })
                    }
                  </>
                )
              }
              {
                showProducts && (
                  <>
                    <StyledSubHeader>
                      {productsName}
                    </StyledSubHeader>
                    {
                      products.map((product) => {
                        const {
                          name,
                          image,
                        } = product;
                        return (
                          <GenericSearchResultItem
                            key={product.id}
                            id={`${product.id}`}
                            logo={findAvailableLocale(image, locale)}
                            to={Routes.productDetail(product.id)}
                            name={findAvailableLocale(name, locale)}
                            variant="compact"
                          />
                        );
                      })
                    }
                  </>
                )
              }
            </ListContainer>
            <ViewAllResultsItem onClick={navigateToAllResults}>
              View all search results
            </ViewAllResultsItem>
          </>
        ) : (
          <EmptyStatePlaceholder />
        )
      }
    </RootContainer>
  );
};

SearchResultList.propTypes = {
  navigateToAllResults: PropTypes.func,
};

SearchResultList.defaultProps = {
  navigateToAllResults: () => { },
};

export default SearchResultList;
